import {_serializeSingle, _serializeExcept, _serializeList, serializeHistoryChangeDetail} from './core';
import type {ServerData, ServerRecord} from './core';
import type {DomainOf} from '../../../types/domain';
import type {WithLabels, WithDeletedParticipant, WithChanges, WithLabelKey} from './_extras';
import {serializeLabelList, serializeMemberBff, serializeDeletedParticipant, serializeDepartment} from './member';

// Seal
/** 스키마 밖에서 조립되는 관계 필드 — 서버 응답 계약의 일부. */
type SealBffExtras = WithLabels & WithDeletedParticipant & WithLabelKey & {
    sealManagers?: unknown[];
    sealApprovers?: unknown[];
};
type HistoryExtras = WithChanges;
type SealLedgerExtras = {member?: unknown};

export function serializeSeal(data: ServerData) {
    return _serializeSingle("seal", data);
}

export function serializeSealList(data: ServerData) {
    return _serializeList("seal", data);
}

/**
 * type 을 싣지 않는 seal 응답
 * - BFF 는 labels 에서 뽑은 label.SEAL_TYPE 사용
 */
const SEAL_TYPE_OMIT = ["type"] as const;

export function serializeSealUntyped(data: ServerData) {
    return _serializeExcept("seal", data, SEAL_TYPE_OMIT);
}

/** system 응답이 싣지 않는 seal 필드 */
const SYSTEM_SEAL_OMIT = ["data", "fileSize", "fileHash", "enabledSealApproverFullApproval"] as const;

export function serializeSealSystem(data: ServerData) {
    return _serializeExcept("seal", data, SYSTEM_SEAL_OMIT);
}

// Seal BFF
export function serializeSealBff(data: ServerData) {
    if (!data) return null;
    const seal = _serializeExcept("seal", data, SEAL_TYPE_OMIT) as DomainOf<"seal"> & SealBffExtras;
    if (data.labels) seal.labels = serializeLabelList(data.labels);

    // 삭제자 스냅샷
    if (data.deletedParticipant) seal.deletedParticipant = serializeDeletedParticipant(data.deletedParticipant);

    // 날인 담당자 (서버: sealManagers = SealManagerSagaAdminInternalWebResponse[{ member: MemberBff }])
    if (data.sealManagers && Array.isArray(data.sealManagers)) {
        seal.sealManagers = data.sealManagers.map(m => serializeSealManager(m));
    }
    // 사용 검토 담당자 (서버: sealApprovers = SealApproverSagaAdminInternalWebResponse[{ order, member: MemberBff }])
    if (data.sealApprovers && Array.isArray(data.sealApprovers)) {
        seal.sealApprovers = data.sealApprovers.map(a => serializeSealApprover(a));
    }

    // label.SEAL_TYPE 설정
    if (seal.labels && Array.isArray(seal.labels)) {
        const sealTypeLabel = (seal.labels as ServerRecord[]).find((label) => label['label.type'] === 'SEAL_TYPE');
        if (sealTypeLabel) {
            seal['label.SEAL_TYPE'] = sealTypeLabel;
        }
    }

    return seal;
}

export function serializeSealBffList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeSealBff(item));
}

// Seal History
/** 이력 응답이 싣지 않는 필드 */
const SEAL_HISTORY_OMIT = ["workspaceId"] as const;

export function serializeSealHistory(data: ServerData) {
    if (!data) return null;
    const history = _serializeExcept("sealHistory", data, SEAL_HISTORY_OMIT) as
        DomainOf<"sealHistory"> & HistoryExtras;
    if (Array.isArray(data.changes)) {
        const serializedChanges = data.changes.map((item: ServerRecord) => ({
            ...serializeHistoryChangeDetail(item),
            ..._serializeSingle("sealHistoryDetail", item),
        }));
        history.changes = serializedChanges;
    }
    return history;
}

export function serializeSealHistoryList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeSealHistory(item));
}

/**
 * 인장 담당자에 묶인 부서를 도메인 키로 변환
 */
function assignSealDepartment(result: ServerRecord, data: ServerData, prefix: "sealApprover" | "sealManager") {
    if (!data?.department) return;
    const department = serializeDepartment(data.department) as ServerRecord;
    result.department = department;
    result[`${prefix}.departmentId`] = department["department.id"];
}

// Seal Approver (서버: { order, member: MemberBff, department: Department })
export function serializeSealApprover(data: ServerData) {
    if (!data) return null;
    const result = _serializeSingle("sealApprover", data) as ServerRecord;
    if (data.member) {
        const member = serializeMemberBff(data.member);
        Object.assign(result, member);
    }
    assignSealDepartment(result, data, "sealApprover");
    return result;
}

export function serializeSealApproverList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeSealApprover(item));
}

// Seal Manager (서버: { member: MemberBff, department: Department })
export function serializeSealManager(data: ServerData) {
    if (!data) return null;
    const result = _serializeSingle("sealManager", data) as ServerRecord;
    if (data.member) {
        const member = serializeMemberBff(data.member);
        Object.assign(result, member);
    }
    assignSealDepartment(result, data, "sealManager");
    return result;
}

export function serializeSealManagerList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeSealManager(item));
}

// SealLedger
export function serializeSealLedger(data: ServerData) {
    return _serializeSingle("sealLedger", data);
}

export function serializeSealLedgerList(data: ServerData) {
    return _serializeList("sealLedger", data);
}

// SealLedger BFF (member 포함)
export function serializeSealLedgerBff(data: ServerData) {
    if (!data) return null;
    const sealLedger: DomainOf<"sealLedger"> & SealLedgerExtras = _serializeSingle("sealLedger", data);
    if (data.member) {
        sealLedger.member = _serializeSingle("member", data.member);
    }
    return sealLedger;
}

export function serializeSealLedgerBffList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeSealLedgerBff(item));
}
